import { Beacon, BeaconListResponse } from "types/beacon";
import { RootStore } from "./RootStore";
import { action, computed, makeObservable, observable } from "mobx";
import { KeyValuePair } from "utils/common";
import { getAssignedBeaconsList, getBeaconList } from "api/beacons.api";
import { ErrorResponseDto } from "api/config/ErrorResponseDto";
import { keys, min } from "lodash";
import { format } from "date-fns";
import { NUMBER_OF_ROWS_PER_PAGE } from "config/constants";

export class BeaconListStore {

  root: RootStore;
  loading = true;
  error: ErrorResponseDto | null = null
  beaconList: Beacon[] = [];
  selectedBeacon: Beacon | null = null

  totalBeaconsCount = 0
  totalUnfilterBeaconsCount = 0
  nextToken: string = ""
  searchTerm: string = ""
  searchCategory: string = "";
  searchCategoriesList: KeyValuePair[] = [
    { value: 'asset_id_filter', key: 'Asset ID' },
    { value: 'beacon_id_filter', key: 'Beacon ID' },
    { value: 'source_filter', key: 'Source' }
  ]
  AssignmentSearchOptions: KeyValuePair<string>[] = []
  assignFilter: "all" | "unassigned" | "assigned" = "all";
  sortField: string = "beacon_id"
  sortDirection: "asc" | "desc" = "asc"
  latestUpdateTime: Date = new Date()

  constructor(root: RootStore) {
    this.root = root;

    makeObservable(this, {
      loading: observable,
      error: observable,
      beaconList: observable,
      totalBeaconsCount: observable,
      totalUnfilterBeaconsCount: observable,
      nextToken: observable,
      searchTerm: observable,
      searchCategory: observable,
      AssignmentSearchOptions: observable,
      selectedBeacon: observable,
      assignFilter: observable,
      latestUpdateTime: observable,

      reset: action.bound,
      loadBeacons: action.bound,
      loadData: action.bound,
      setSelectedBeacon: action.bound,
      onSort: action.bound,
      setSearchTerm: action.bound,
      setSearchCategory: action.bound,
      setAssignFilter: action.bound,

      nextCount: computed,
      getEmptyListMsg: computed,
      getLatestUpdateText: computed,
    });
  }

  reset() {
    this.loading = true;
    this.beaconList = []
    this.selectedBeacon = null
    this.error = null
    this.totalBeaconsCount = 0
    this.nextToken = ""
    this.searchTerm = ""
    this.searchCategory = "";
    this.sortField = "beacon_id"
    this.sortDirection = "asc"
    this.AssignmentSearchOptions = []
    this.assignFilter = "all"
  }

  setSelectedBeacon(beacon: Beacon | null) {
    this.selectedBeacon = beacon
  }

  setSearchTerm(search: string) {
    this.searchTerm = search
  }
  setSearchCategory(cat: string) {
    this.searchCategory = cat
  }

  setAssignFilter(value: "all" | "unassigned" | "assigned") {
    this.assignFilter = value
  }

  setLatestUpdateTime() {
    this.latestUpdateTime = new Date()
  }

  getLoadParams(firstPage: boolean): Record<string, any> {
    const params: Record<string, any> = {
      limit: NUMBER_OF_ROWS_PER_PAGE,
    }
    if (!firstPage && this.nextToken !== "")
      params.next = this.nextToken

    if (this.searchTerm !== "") {
      if (this.searchCategory !== "") {
        if (this.searchCategory === 'is_outdoors')
          params.outdoors_filter = 'true'
        else
          params[this.searchCategory] = this.searchTerm;
      }
      else {
        this.searchCategoriesList.forEach(c => {
          if (c.value !== "is_outdoors")
            params[c.value] = this.searchTerm
        })
      }
    }

    if (this.assignFilter !== "all") {
      params["is_assigned_to_asset_filter"] = this.assignFilter === "assigned"
    }

    if (this.sortField !== "") {
      params.sort = `${this.sortDirection === "desc" ? '-' : ''}${this.sortField}`
    }
    return params
  }

  paramsIncludeFilter(params: Record<string, any>): boolean {
    return keys(params).some(key => key !== "limit" && key !== "next" && key !== "sort")
  }

  loadData() {
    this.loading = true
    this.error = null
    return Promise.all([
      this.loadBeacons(true),
      getAssignedBeaconsList()
    ])
      .then(res => {
        this.AssignmentSearchOptions = [
          { value: 'all', key: `All (${this.totalBeaconsCount})` },
          { value: 'assigned', key: `Asset assigned (${res[1].total})` },
          { value: 'unassigned', key: `Asset not assigned (${this.totalBeaconsCount - res[1].total})` },
        ]
        this.setLatestUpdateTime()
      })
      .catch((err: ErrorResponseDto) => {
        this.error = err
      })
  }

  loadBeacons(firstPage = false) {
    this.loading = true
    const params = this.getLoadParams(firstPage)
    return getBeaconList(params)
      .then((res: BeaconListResponse) => {
        if (firstPage)
          this.beaconList = [...res.items]
        else
          this.beaconList = [...this.beaconList, ...res.items]
        this.totalBeaconsCount = res.total
        this.nextToken = res.next
        this.error = null
        if (!this.paramsIncludeFilter(params))
          this.totalUnfilterBeaconsCount = res.total
      })
      .catch((err: ErrorResponseDto) => {
        this.error = err
      })
      .finally(() => this.loading = false);
  }

  onSort(field: string) {
    this.sortDirection = this.sortField !== field
      ? "asc"
      : this.sortDirection === "asc" ? "desc" : "asc"
    this.sortField = field
    this.loadBeacons(true)
  }

  get getEmptyListMsg() {
    if (this.searchTerm === "")
      return "No Beacons to display"
    else
      return `Sorry, no results were found for ${this.searchTerm}
          ${this.searchCategory !== ""
          ? ` in <b>${this.searchCategoriesList.find(c => c.value === this.searchCategory)?.key}</b>`
          : ''
        }`
  }

  get nextCount() {
    return min([this.totalBeaconsCount - this.beaconList.length, NUMBER_OF_ROWS_PER_PAGE]) || 0
  }

  get getLatestUpdateText() {
    const year = format(this.latestUpdateTime, 'dd/MM/yyyy');
    const time = format(this.latestUpdateTime, 'HH:mm');
    return `Last updated at ${time} on ${year}`;
  }

}